import { useState } from 'react'

import { Box, Section, useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, Pencil } from '@factorialco/f0-react/icons/app'
import {
  AtsCandidateSourceDataFragment,
  AtsCandidateSourcesDocument,
  DeleteCandidateSourceDocument,
  DeleteCandidateSourceMutation,
} from 'generated/resources/graphql'
import { useToggle } from 'usehooks-ts'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EditCandidateSourceModal } from './components/EditCandidateSourceModal'
import { NewCandidateSourceModal } from './components/NewCandidateSourceModal'

export const SourcesCustomization = () => {
  const { openDialog } = useConfirmationDialog()
  const [showCreateModal, toggleCreateModal] = useToggle(false)
  const [showEditSourceModal, setEditSourceModal] = useState(false)
  const [source, setSource] = useState<AtsCandidateSourceDataFragment>()

  const [deleteCandidateSource] = useMutation(DeleteCandidateSourceDocument, {
    extractorFn: (data: DeleteCandidateSourceMutation) =>
      data.atsMutations.deleteAtsCandidateSource,
  })
  const onDelete = async (id: string) => {
    const response = await deleteCandidateSource({
      variables: { id },
      update(cache) {
        cache.evict({ id: cache.identify({ id, __typename: 'AtsCandidateSource' }) })
      },
    })

    const result = handleResult(response, () => {})

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    }
  }

  const candidateSourcesSource = useGraphqlDataCollectionSource({
    document: AtsCandidateSourcesDocument,
    mapData: (data) => data.ats.candidateSourcesConnection.nodes,
    mapPagination: (data) => ({
      total: data.ats.candidateSourcesConnection.totalCount ?? 0,
    }),
    mapVariables: ({ sortings, search }) => {
      return {
        sort: sortings?.[0],
        search: search,
      }
    },
    primaryActions: () => ({
      label: i18n.t('ats.manage_sources_customization.add_source'),
      onClick: toggleCreateModal,
      icon: Add,
    }),
    itemActions: (item) => {
      if (item.editable) {
        return [
          {
            label: i18n.t('common.edit'),
            onClick: () => {
              setSource(item)
              setEditSourceModal(true)
            },
            icon: Pencil,
          },
          {
            label: i18n.t('common.delete'),
            onClick: () => {
              openDialog({
                illustration: 'alert',
                title: i18n.t('ats.manage_sources_customization.delete_modal.title'),
                content: i18n.t('ats.manage_sources_customization.delete_modal.description'),
                confirmLabel: i18n.t(
                  'ats.manage_sources_customization.delete_modal.confirm_button'
                ),
                cancelLabel: i18n.t('common.cancel'),
                onConfirm: () => onDelete(item.id),
              })
            },
            icon: Delete,
            critical: true,
          },
        ]
      }
      return []
    },
    sortings: {
      name: {
        label: i18n.t('ats.manage_sources_customization.table_headers.name'),
      },
      category: {
        label: i18n.t('ats.manage_sources_customization.table_headers.category'),
      },
      editable: {
        label: i18n.t('ats.manage_sources_customization.table_headers.type'),
      },
    },
    defaultSortings: {
      field: 'category',
      order: 'asc',
    },
    search: {
      enabled: true,
    },
  })

  const getCandidateSourceCategoryLabel = (category: string) => {
    switch (category) {
      case 'job_board':
        return i18n.t('ats.manage_sources_customization.categories.job_board')
      case 'agency_or_external_recruiter':
        return i18n.t('ats.manage_sources_customization.categories.agency_or_external_recruiter')
      case 'event':
        return i18n.t('ats.manage_sources_customization.categories.event')
      case 'social_media':
        return i18n.t('ats.manage_sources_customization.categories.social_media')
      case 'referral':
        return i18n.t('ats.manage_sources_customization.categories.referral')
      case 'internal':
        return i18n.t('ats.manage_sources_customization.categories.internal')
      case 'organic':
        return i18n.t('ats.manage_sources_customization.categories.organic')
      case 'manually_added':
        return i18n.t('ats.manage_sources_customization.categories.manually_added')
      case 'system':
        return i18n.t('ats.manage_sources_customization.categories.system')
    }
  }

  const type = (candidateSource: AtsCandidateSourceDataFragment) =>
    candidateSource.editable
      ? i18n.t('ats.manage_sources_customization.types.customized')
      : i18n.t('ats.manage_sources_customization.types.pre_configured')

  return (
    <>
      <Section
        illustration="network-group"
        title={i18n.t('ats.manage_sources_customization.title')}
        description={i18n.t('ats.manage_sources_customization.description')}
      />
      {source && (
        <>
          {showEditSourceModal && (
            <EditCandidateSourceModal onClose={() => setEditSourceModal(false)} source={source} />
          )}
        </>
      )}
      <Box paddingX="s64" paddingBottom="s32">
        <OneDataCollection
          source={candidateSourcesSource}
          visualizations={[
            {
              type: 'table',
              options: {
                columns: [
                  {
                    label: i18n.t('ats.manage_sources_customization.table_headers.name'),
                    sorting: 'name',
                    render: (item) => ({
                      type: 'text',
                      value: item.label,
                    }),
                  },
                  {
                    label: i18n.t('ats.manage_sources_customization.table_headers.category'),
                    sorting: 'category',
                    render: (item) => ({
                      type: 'text',
                      value: getCandidateSourceCategoryLabel(item.category),
                    }),
                  },
                  {
                    label: i18n.t('ats.manage_sources_customization.table_headers.type'),
                    sorting: 'editable',
                    render: (item) => ({
                      type: 'text',
                      value: type(item),
                    }),
                  },
                ],
              },
            },
          ]}
        />
      </Box>
      {showCreateModal && <NewCandidateSourceModal onClose={toggleCreateModal} />}
    </>
  )
}
